import { FC, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';

import { logOut } from '@src/store/auth/auth-slice';
import { useAppDispatch } from '@src/store/store';

import useLocales from '@src/locales/use-locales';
import paths from '@src/routes/routes';
import { UserType } from '@src/types/main';

type HeaderAccountInfoProps = {
  user: UserType;
};

const HeaderAccountInfo: FC<HeaderAccountInfoProps> = (props) => {
  const { t } = useLocales();
  const { user } = props;

  const dispatch = useAppDispatch();

  const { firstName, lastName, role } = user;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const subTitle =
    role === 'ADMIN' ? t('account.administrator') : t('account.user');

  const avatarLetter = firstName?.at(0)?.toUpperCase();

  const open = Boolean(anchorEl);
  const id = open ? 'user info popover' : undefined;
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = () => {
    dispatch(logOut());
  };

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <Avatar
          aria-describedby={id}
          sx={{ backgroundColor: 'primary.dark', color: 'common.white' }}
        >
          <Typography
            sx={{
              fontFamily: 'secondaryFontFamily',
              fontSize: '12px',
              fontWeight: 'fontWeightSemiBold',
              color: 'common.white',
            }}
          >
            {avatarLetter}
          </Typography>
        </Avatar>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 150,
              py: 1,
            },
          },
        }}
      >
        <Stack gap={0.5}>
          <Typography
            sx={{ px: 1 }}
            variant="button"
          >{`${firstName} ${lastName}`}</Typography>
          <Typography
            sx={{
              fontFamily: 'fontFamily',
              fontSize: '12px',
              letterSpacing: 0.15,
              color: 'custom.grey',
              px: 1,
            }}
          >
            {subTitle}
          </Typography>
          <Divider />
          <Button
            size="small"
            sx={{
              display: 'flex',
              gap: 0.5,
              p: 1,
              m: 0,
              justifyContent: 'start',
              color: 'custom.text',
            }}
            component={RouterLink}
            to={paths.dashboard.profile}
          >
            {t('buttons.profile')}
          </Button>
          <Divider />
          <Button
            size="small"
            sx={{
              display: 'flex',
              gap: 0.5,
              p: 1,
              m: 0,
              justifyContent: 'start',
            }}
            onClick={handleLogOut}
          >
            {t('buttons.signOut')}
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
};

export default HeaderAccountInfo;
